if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;
      if (evt.submitter?.matches('[data-sd-add-to-cart]')) return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (this.requiresVerification()) {
          const oldCartLine = oldCartData.items.find((item) => item.key === data.key);
          const previousQuantity = oldCartLine ? oldCartLine.quantity : 0;
          const keepItem = await ProductForm.showVerificationModal();

          if (!keepItem) {
            const rollbackResponse = await fetch(theme.routes.cartChange, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                id: data.key,
                quantity: previousQuantity,
                sections: sections.split(','),
                sections_url: window.location.pathname
              })
            });

            const rollbackData = await rollbackResponse.json();

            if (!rollbackResponse.ok) {
              throw new Error(
                rollbackData.description || rollbackData.message || rollbackResponse.status
              );
            }

            if (rollbackData.sections) {
              ProductForm.updateCartIcon(rollbackData);

              if (this.cartDrawer) {
                await this.cartDrawer.renderContents(rollbackData, false);
              }
            }

            if (!this.cartDrawer && window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (rollbackData.item_count === 0) {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }

            return;
          }
        }

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Returns true when the product form belongs to a restricted product.
     * @returns {boolean}
     */
    requiresVerification() {
      return Boolean(
        this.form && this.form.querySelector('[data-verification-required="true"]')
      );
    }

    /**
     * Shows the editable restricted-product verification modal.
     * @returns {Promise<boolean>} true keeps the item; false removes the newly added quantity.
     */
    static showVerificationModal() {
      const modal = document.getElementById('verification-required-modal');

      if (!modal) return Promise.resolve(true);

      const okButton = modal.querySelector('[data-verification-ok]');
      const cancelButton = modal.querySelector('[data-verification-cancel]');
      const backdrop = modal.querySelector('[data-verification-backdrop]');

      if (!okButton || !cancelButton) return Promise.resolve(true);

      const previousFocus = document.activeElement;

      modal.hidden = false;
      document.body.classList.add('verification-modal-open');
      okButton.focus();

      return new Promise((resolve) => {
        const finish = (keepItem) => {
          modal.hidden = true;
          document.body.classList.remove('verification-modal-open');

          document.removeEventListener('keydown', handleKeydown);
          okButton.removeEventListener('click', handleOk);
          cancelButton.removeEventListener('click', handleCancel);
          if (backdrop) backdrop.removeEventListener('click', handleCancel);

          if (previousFocus && typeof previousFocus.focus === 'function') {
            previousFocus.focus();
          }

          resolve(keepItem);
        };

        const handleOk = () => finish(true);
        const handleCancel = () => finish(false);
        const handleKeydown = (event) => {
          if (event.key === 'Escape') finish(false);
        };

        okButton.addEventListener('click', handleOk);
        cancelButton.addEventListener('click', handleCancel);
        if (backdrop) backdrop.addEventListener('click', handleCancel);
        document.addEventListener('keydown', handleKeydown);
      });
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
